<template>
  <div class="container order-content">
    <b-loading :active.sync="isLoading" />
    <b-modal :active.sync="isSettling" :can-cancel="false">
      <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
          <p class="modal-card-title">決済中...</p>
        </header>
        <section class="modal-card-body">
          <b-field
            label="現在テストネット環境ではETHでの決済処理に2~3分の時間がかかっています。お手数ですが、このままお待ちください。"
          />
        </section>
      </div>
    </b-modal>
    <div style="text-align: -webkit-center;">
      <!-- ステップ -->
      <div class="step-order">
        <div class="steps">
          <div
            :class="{
              'is-active': stepActive === 0,
              'is-completed': stepActive >= 1
            }"
            class="step-item"
          >
            <div class="step-marker">1</div>
            <div class="step-details"><p class="step-title">情報入力</p></div>
          </div>
          <div
            :class="{
              'is-active': stepActive === 1,
              'is-completed': stepActive === 2
            }"
            class="step-item"
          >
            <div class="step-marker">2</div>
            <div class="step-details"><p class="step-title">注文確認</p></div>
          </div>
          <div :class="{ 'is-active': stepActive === 2 }" class="step-item">
            <div class="step-marker">3</div>
            <div class="step-details"><p class="step-title">購入完了</p></div>
          </div>
        </div>
      </div>
    </div>

    <div style="margin-top: 30px">
      <div class="tile is-ancestor">
        <div :class="stepActive !== 2 ? 'is-8' : ''" class="step-container tile is-vertical">
          <div v-if="stepActive == 0">
            <div class="tile is-parent mb-60">
              <article class="tile is-child">
                <!-- Grid Item -->
                <div v-if="!isEdit">
                  <h5 class="c-text_title text-header">{{ $t('C023_2') }}</h5>
                  <message-area />
                  <div v-if="deliveryAdds.length > 0" class="columns is-multiline is-mobile is-variable">
                    <div
                      v-for="(address, index) in deliveryAdds"
                      :key="index"
                      class="column is-12-mobile is-6-tablet is-6-desktop"
                    >
                      <info-postcode
                        :address="address"
                        :item-index="index"
                        :remove-address="removeAddress"
                        :select-delivery-address="selectDeliveryAddress"
                        :is-disabled="totalPrice === 0"
                      />
                    </div>
                  </div>
                  <h5 v-else class="c-text_title mt-30">
                    {{ $t('EA2014_Content') }}
                  </h5>
                </div>

                <div class="mt-30">
                  <h5 class="c-text_title text-header">
                    {{ isEdit ? $t('C024_2') : $t('C023_9') }}
                  </h5>
                  <message-area v-if="isEdit" :message-id="messageId" :error-id="errorId" />
                  <div class="tile is-ancestor">
                    <div class="tile is-vertical is-8">
                      <section>
                        <b-field :label="$t('C023_10')">
                          <b-custom-input
                            ref="receiverName"
                            v-model="selectedAddress.receiverName"
                            :placeholder="$t('C023_11')"
                            :validate-function="validateInputName"
                          />
                        </b-field>
                        <div style="display: flex">
                          <b-field :label="$t('C023_12')">
                            <b-custom-input
                              ref="zipcode"
                              v-model="selectedAddress.zipcode"
                              :validate-function="validatePostcode"
                              :placeholder="$t('C023_13')"
                              mask="111-1111"
                              style="width: 120px"
                            />
                          </b-field>
                          <div class="text-postcode">
                            <a
                              href="http://www.post.japanpost.jp/zipcode/"
                              class="line-height40 extra-link"
                              target="_blank"
                              >{{ $t('C005_7') }}
                              <b-icon pack="fas" icon="external-link-alt" size="is-small" />
                            </a>
                          </div>
                        </div>

                        <b-field :label="$t('C023_15')">
                          <b-select v-model="selectedAddress.prefectureId" :placeholder="$t('C023_16')" expanded>
                            <optgroup v-for="group in $t('areas')" :label="group.name" :key="group.id">
                              <option
                                v-for="item in group.prefectures"
                                :value="group.id + '_' + item.id"
                                :key="item.id"
                              >
                                {{ item.name }}
                              </option>
                            </optgroup>
                          </b-select>
                        </b-field>
                        <p v-if="showSelectError" class="help is-danger">
                          {{ $t('EF2055_Content') }}
                        </p>

                        <b-field :label="$t('C005_10')">
                          <b-custom-input
                            ref="address"
                            v-model="selectedAddress.address"
                            :placeholder="$t('C005_11')"
                            :validate-function="validateAddress"
                          />
                        </b-field>

                        <b-field :label="$t('C023_19')">
                          <b-custom-input
                            ref="phoneNumber"
                            v-model="selectedAddress.phoneNumber"
                            :placeholder="$t('C023_20')"
                            :validate-function="validatePhoneNumber"
                            type="number"
                          />
                        </b-field>

                        <div class="field">
                          <b-checkbox v-model="selectedAddress.saveRecord" type="is-primary">{{
                            $t('C023_21')
                          }}</b-checkbox>
                        </div>
                      </section>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <div class="tile is-parent mb-60">
              <button v-if="isEdit" class="button is-primary" @click="updateAddress">
                {{ $t('C024_21') }}
              </button>
              <button v-else class="button is-primary" @click="createAddress">
                {{ $t('C024_21') }}
              </button>
            </div>
          </div>

          <div v-if="stepActive === 1">
            <div class="tile is-parent mb-60">
              <article class="tile is-child">
                <h5 class="c-text_title text-header">ご注文内容の確認</h5>
                <message-area />
                <h6 class="c-text_title font-size-16">お届け先</h6>
                <div class="item-border-bottom">
                  <table>
                    <tr>
                      <td class="font-size-14">
                        <div style="max-width: 500px;">
                          <p>{{ selectedAddress.receiverName }}</p>
                          <p>〒 {{ selectedAddress.zipcode }}</p>
                          <p>{{ selectedAddress.prefecture }}</p>
                          <p v-line-clamp="2" class="desc">{{ selectedAddress.address }}</p>
                        </div>
                      </td>
                      <td style="float: right">
                        <button class="button" @click="goBack">
                          {{ $t('C025_7') }}
                        </button>
                      </td>
                    </tr>
                  </table>
                </div>
                <div class="tile" style="margin-top: 30px">
                  <div class="tile is-8">
                    <h6 class="text-bold c-text_title">ご注文商品</h6>
                  </div>
                  <div class="tile" style="display: block">
                    <router-link
                      :to="{
                        name: 'CustomerCartDetail'
                      }"
                      class="tile is-pulled-right"
                    >
                      <button class="button">{{ $t('C025_7') }}</button>
                    </router-link>
                  </div>
                </div>
                <item-order v-for="(item, index) in cartData" :item="item" :key="index" />
              </article>
            </div>

            <div style="padding: 0.75rem">
              <h6 class="c-text_title mb-30 font-size-16">お支払い方法</h6>

              <p>ETH決済</p>
            </div>
          </div>

          <div v-if="stepActive === 2">
            <div class="content-completed text-center">
              <h1 class="font-weight-bold margin-top_30">
                ご注文ありがとうございました
              </h1>
              <br /><br />
              この度は{{ $appName }}をご利用いただきありがとうございます。<br /><br />
              ご注文番号：<span class="font-weight-bold" v-html="orderIds"></span><br /><br />
              <div class="tile is-ancestor mt-30">
                <div class="is-middle">
                  <div class="tile">
                    <div class="tile is-parent is-6">
                      <router-link
                        :to="{
                          name: 'CustomerOrderList'
                        }"
                        class="tile"
                      >
                        <button class="button" style="height: 48px; width: 100%">
                          {{ $t('C026_6') }}
                        </button>
                      </router-link>
                    </div>
                    <div class="tile is-parent is-6">
                      <router-link
                        :to="{
                          name: 'TopPage'
                        }"
                        class="tile"
                      >
                        <button class="button is-primary" style="height: 48px; width: 100%">
                          {{ $t('C026_7') }}
                        </button>
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="stepActive !== 2" class="price-container tile is-parent">
          <article class="tile is-child is-4">
            <div style="width: 300px">
              <div class="c-bg-grey price-right">
                <table>
                  <tr class="bill-row">
                    <td>{{ $t('C023_22') }}</td>
                    <td class="text-right">{{ formatPrice(totalProduct) }} {{ $t('common.ETH') }}</td>
                  </tr>
                  <tr class="bill-row last-row">
                    <td>{{ $t('C024_17') }}</td>
                    <td class="text-right">{{ formatPrice(totalShip) }} {{ $t('common.ETH') }}</td>
                  </tr>
                  <tr class="bill-row total-row">
                    <td>{{ $t('C024_19') }}</td>
                    <td class="text-right">{{ formatPrice(totalPrice) }} {{ $t('common.ETH') }}</td>
                  </tr>
                  <tr v-if="stepActive === 1">
                    <td colspan="2">
                      <button class="button is-primary btn-r mt-30" @click="confirmOrder">
                        {{ $t('C025_14') }}
                      </button>
                    </td>
                  </tr>
                  <tr class="bill-row">
                    <td colspan="2">
                      <div v-if="showNote" class="note-message text-center">
                        注文価格と比べて高い送料となっています。<br />ご注意ください。
                      </div>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" src="./order-settlement.scss">
</style>

<script>
import InfoPostcode from './info-postcode/info-postcode';
import {
  addDeliveryAddress,
  getDeliveryAddressInfo,
  removeDeliveryAddress,
  updateDeliveryAddress
} from '../../../../services/profile.service';
import MessageArea from '@/components/common/message-area/message-area.vue';
import BCustomInput from '@/components/common/b-custom-input/b-custom-input.vue';
import ItemOrder from './item-order/item-order';
import {
  checkError,
  validateCodeNumber,
  validateEmptyValue,
  validateInputValue,
  validateUpperRange,
  validateValueRange
} from '../../../../utils/commonUtil';
import { estimateFee } from '../../../../services/fee.service';
import { isMainNetwork, isMetaMaskEnabled, paymentConfirms } from '../../../../services/payment.service';
import { confirmOrders } from '../../../../services/order.service';
import messageUtils from '../../../../utils/mixins/messageUtils';
import { removeProductByIds } from '../../../../services/cart.service';
import dataTransform from '../../../../utils/mixins/dataTransform';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';

export default {
  name: 'AddressRegistration',
  components: {
    'message-area': MessageArea,
    'info-postcode': InfoPostcode,
    'b-custom-input': BCustomInput,
    'item-order': ItemOrder
  },
  mixins: [messageUtils, dataTransform, validationMixin],
  props: {
    carts: {
      type: Array,
      default: () => []
    }
  },
  validations: {
    selectedAddress: {
      prefectureId: {
        required
      }
    }
  },
  data() {
    return {
      loading: 0,
      isSettling: false,
      isEdit: false,
      isConfirmed: false,
      stepActive: 0,
      cartData: this.carts,
      deliveryAdds: [],
      orders: [],
      selectedIndex: null,
      prefectureError: '',
      selectedAddress: {
        prefectureId: null,
        saveRecord: false
      },
      moreText: true
    };
  },
  computed: {
    prefectureId: {
      cache: false,
      get() {
        return this.selectedAddress.prefectureId;
      }
    },
    prefecture: {
      cache: false,
      get() {
        return this.selectedAddress.prefecture;
      }
    },
    isValidated: function() {
      const { success } = this.validateSelectedAddress(this.selectedAddress);
      return success || false;
    },
    totalProduct: function() {
      let sum = 0;
      this.cartData.forEach(item => {
        if (item.product) {
          sum += item.quantity * item.product.price || 0;
        }
      });
      return sum;
    },
    totalShip: function() {
      let sum = 0;
      this.cartData.forEach(item => {
        if (item.shipFee) {
          sum += Number(item.shipFee);
        }
      });
      return sum;
    },
    totalPrice: function() {
      return this.totalProduct + this.totalShip;
    },
    orderIds: function() {
      const orders = [];
      this.orders.forEach(item => {
        if (item.txHash) {
          orders.push(`<a href="/customer/order/detail/${item.orderId}">${item.orderId}</a>`);
        }
      });
      // "1, 2, 3"
      // <a href=""></a>
      return orders.join(', ');
    },
    showSelectError: function() {
      return (
        this.$v.selectedAddress.prefectureId.$error &&
        !this.$v.selectedAddress.prefectureId.required &&
        Object.keys(this.selectedAddress).length
      );
    },
    showNote: function() {
      let invalid = false;
      if (this.cartData && this.cartData.length) {
        for (let i = 0; i < this.cartData.length; i++) {
          console.log(
            JSON.stringify(
              {
                id: i,
                totalPrice: this.cartData[i].totalPrice,
                shipFee: this.cartData[i].shipFee
              },
              null,
              2
            )
          );
          if (
            this.cartData[i] &&
            'shipFee' in this.cartData[i] &&
            'totalPrice' in this.cartData[i] &&
            this.cartData[i].totalPrice - 2 * this.cartData[i].shipFee < 0
          ) {
            invalid = true;
            break;
          }
        }
      }
      return invalid;
    }
  },
  watch: {
    prefectureId: {
      handler: function(value) {
        if (!value) return;
        const areas = this.$t('areas');
        const indexes = value.split('_');
        if (indexes[0] && indexes[1]) {
          this.selectedAddress['prefecture'] = areas[indexes[0]].prefectures[indexes[1]].name;
        }
      },
      cache: false
    },
    prefecture: {
      handler: async function(value) {
        this.loading++;
        const { status, result, error } = await estimateFee(this.cartData, value);
        this.loading--;
        if (status && result) {
          this.cartData = result.data;
        }
      }
    }
  },
  async mounted() {
    if (this.carts.length < 1) {
      return this.$router.push({
        name: 'CustomerCartDetail'
      });
    }
    this.loading++;
    const { status, result } = await getDeliveryAddressInfo();
    this.loading--;
    if (status) {
      this.deliveryAdds = result.data;
    } else {
      this.errorId = result.messageId;
    }
  },
  methods: {
    async removeAddress(addressId) {
      this.clearMessage();
      this.loading++;
      const { status, result } = await removeDeliveryAddress(addressId);
      this.loading--;
      if (status) {
        this.deliveryAdds = result.data;
      } else {
        this.errorId = result.messageId;
      }
    },
    next() {
      if (this.stepActive++ > 2) this.stepActive = 0;
    },
    goBack() {
      this.clearMessage();
      this.isEdit = false;
      this.selectedAddress = {};
      this.stepActive = 0;
    },
    checkSubmitForm() {
      this.$v.$touch();
      this.$refs.receiverName.onBlur();
      this.$refs.zipcode.onBlur();
      this.$refs.address.onBlur();
      this.$refs.phoneNumber.onBlur();

      return checkError(this.$refs) && !this.$v.$invalid;
    },
    async createAddress() {
      this.clearMessage();
      if (!this.checkSubmitForm()) {
        return;
      }
      if (this.selectedAddress.saveRecord) {
        this.loading++;
        const address = this.selectedAddress;
        const { status, result } = await addDeliveryAddress(
          address.receiverName,
          address.zipcode,
          address.prefecture,
          address.address,
          address.phoneNumber
        );
        this.loading--;
        if (status) {
          this.deliveryAdds = result.data;
        } else {
          this.updateMessage(status, result);
          parent.scrollTo(0, 0);
          return;
        }
      }
      this.next();
    },
    async updateAddress() {
      if (!this.checkSubmitForm()) {
        return;
      }
      this.clearMessage();
      const { id, receiverName, zipcode, prefecture, address, phoneNumber, saveRecord } = this.selectedAddress;
      if (id !== null && typeof id !== 'undefined' && saveRecord) {
        this.loading++;
        const { status, result } = await updateDeliveryAddress(
          id,
          receiverName,
          zipcode,
          prefecture,
          address,
          phoneNumber
        );
        this.loading--;
        if (status) {
          this.deliveryAdds = result.data;
        } else {
          this.updateMessage(status, result);
          parent.scrollTo(0, 0);
          return;
        }
      }
      this.next();
    },
    async confirmOrder() {
      if (!(await isMetaMaskEnabled())) {
        // eslint-disable-next-line no-alert
        alert(this.$t('EF2149_Content'));
        return;
      }
      if (!(await isMainNetwork())) {
        // eslint-disable-next-line no-alert
        alert(this.$t('EF2150_Content'));
        return;
      }
      let messageId;
      try {
        this.clearMessage();
        this.loading++;
        this.isSettling = true;
        if (!this.isConfirmed) {
          const shipAddress = JSON.stringify(this.selectedAddress);
          const confirmRes = await confirmOrders(this.cartData, shipAddress);
          if (!confirmRes.status) {
            messageId = confirmRes.result && confirmRes.result.messageId;
            throw new Error('Confirm failed !');
          }
          this.orders = confirmRes.result.data;
          this.isConfirmed = true;
          // await resetCart();
          this.removeFromCart(this.orders);
        }

        const paymentRes = await paymentConfirms(this.orders);

        this.orders = paymentRes.result.data;
        if (this.isPaidOrders(this.orders)) {
          this.next();
        }

        this.loading--;
        this.isSettling = false;
      } catch (error) {
        console.error(`order-settlement.vue - (514) - `, error);
        this.loading--;
        this.isSettling = false;
        this.updateMessage(0, { messageId: messageId || 'EA2017_Content' });
        return;
      }
    },
    async removeFromCart(orders) {
      const productIds = orders.map(item => item.productId);
      const res = await removeProductByIds(productIds);
    },
    isPaidOrders(orders) {
      let status = false;
      orders.forEach(item => {
        if (item.txHash) status = true;
      });

      if (!status) this.updateMessage(0, { messageId: 'EF2063_Content' });
      return status;
    },
    selectDeliveryAddress(index, action) {
      this.clearMessage();
      this.selectedIndex = index;
      const prefecture = this.deliveryAdds[index].prefecture;
      const prefIndex = this.getPrefectureIdFromName(prefecture);
      let saveRecord = false;
      if (action === 'toEdit') {
        this.isEdit = true;
        saveRecord = true;
      } else {
        this.stepActive = 1;
      }
      this.selectedAddress = {
        ...this.deliveryAdds[index],
        prefectureId: prefIndex,
        saveRecord
      };

      if (action === 'toEdit') {
        this.$nextTick(() => {
          this.checkSubmitForm();
        });
      }
    },
    validateInputName(value) {
      return validateInputValue(() => {
        validateEmptyValue(value, 'EF2051_Content');
        validateUpperRange(value, 30, 'EF2052_Content');
      });
    },
    validatePostcode(value) {
      return validateInputValue(() => {
        validateEmptyValue(value, 'EF2053_Content');
        validateCodeNumber(value, 'EF2010_Content');
        validateValueRange(value, 8, 'EF2010_Content');
      });
    },
    validateAddress(value) {
      return validateInputValue(() => {
        validateEmptyValue(value, 'EF2011_Content');
        validateUpperRange(value, 100, 'EF2011_Content');
      });
    },
    validatePrefecture(value) {
      return validateInputValue(() => {
        validateEmptyValue(value, 'EF2055_Content');
      });
    },
    validatePhoneNumber(value) {
      return validateInputValue(() => {
        validateEmptyValue(value, 'EF2058_Content');
        validateValueRange(value, [10, 11], 'EF2059_Content');
      });
    },
    validateSelectedAddress(selectedAddress) {
      const { receiverName, zipcode, address, prefecture, phoneNumber, saveRecord } = selectedAddress;
      try {
        validateEmptyValue(receiverName, 'EF2051_Content');
        validateUpperRange(receiverName, 30, 'EF2052_Content');
        validateEmptyValue(zipcode, 'EF2053_Content');
        validateValueRange(zipcode, 7, 'EF2010_Content');
        validateEmptyValue(address, 'EF2011_Content');
        validateUpperRange(address, 100, 'EF2011_Content');
        validateEmptyValue(prefecture, 'EF2055_Content');
        validateEmptyValue(phoneNumber, 'EF2058_Content');
        validateValueRange(phoneNumber, [10, 11], 'EF2059_Content');
        const success = saveRecord || false;
        return { success };
      } catch (error) {
        let messageId = 'EF2001_Content';
        if (error.messageId) {
          messageId = error.messageId;
        }
        return { messageId };
      }
    },
    getPrefectureIdFromName(name) {
      let result = null;
      const areas = this.$t('areas');
      areas.forEach((area, areaIndex) => {
        const prefectures = area.prefectures;
        prefectures.forEach((prefecture, prefIndex) => {
          if (prefecture.name === name) result = `${areaIndex}_${prefIndex}`;
        });
      });
      return result;
    }
  }
};
</script>
